/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Printer, ArrowLeft, Copy, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function NeuroFlowDoctorExport() {
  const { subscriberId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(today);

  const load = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const r = await axios.get(
        `${API}/neuroflow/${subscriberId}/doctor-export`
        + `?from_date=${fromDate}&to_date=${toDate}`,
      );
      setData(r.data);
    } catch (e) {
      setPageError(e?.response?.data?.detail || 'Could not load export');
    } finally {
      setLoading(false);
    }
  }, [subscriberId, fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  const copySummary = () => {
    if (!data) return;
    const lines = [
      `NeuroFlow Doctor Report — ${data.subscriber_id}`,
      `Range: ${data.from_date || '(all)'} → ${data.to_date || '(all)'}`,
      `Generated: ${new Date().toISOString()}`,
      '',
      `Anchors (${data.medicine_anchors?.length || 0})`,
      ...(data.medicine_anchors || []).map(a =>
        `  ${a.planned_time} · ${a.medicine_name} · ${a.medicine_type} · `
        + `${a.strength_label || ''} · protected ${a.protected_window_before_min}/${a.protected_window_after_min}`),
      '',
      `Events (${data.events?.length || 0})`,
      ...(data.events || []).slice(-30).map(e =>
        `  ${e.event_time} · ${e.event_type} · ${e.severity_score ?? ''} ${e.note || ''}`),
      '',
      `Daily snapshots (${data.daily_snapshots?.length || 0})`,
      ...(data.daily_snapshots || []).map(s =>
        `  ${s.date} · ${s.current_state} · gate ${s.gate_status} · `
        + `delayedON ${s.delayed_on_count} · noON ${s.no_on_risk_count} · `
        + `dyskPeak ${s.dyskinesia_peak_score} · OFF ${s.total_off_time_min}m`),
      '',
      `Safety alerts (${data.safety_alerts?.length || 0})`,
      ...(data.safety_alerts || []).map(a =>
        `  ${a.created_at} · ${a.alert_type} · ${a.severity} · ${a.status}`),
    ];
    navigator.clipboard.writeText(lines.join('\n'));
    toast.success('Summary copied to clipboard.');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white text-slate-900 print:bg-white">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          @page { size: A4 portrait; margin: 14mm; }
        }
      `}</style>

      {/* Top bar — hidden on print */}
      <div className="no-print sticky top-0 z-10 bg-slate-900 text-white border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button
          data-testid="export-back-btn"
          onClick={() => navigate(`/admin/neuroflow/${subscriberId}`)}
          className="flex items-center gap-1.5 text-xs text-white/70 hover:text-white"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <div className="text-sm font-medium">Printable Doctor Export</div>
        <div className="ml-auto flex items-center gap-2">
          <input
            data-testid="export-from-date"
            type="date"
            value={fromDate}
            onChange={(e)=>setFromDate(e.target.value)}
            className="bg-slate-800 border border-white/10 rounded px-2 py-1 text-xs text-white"
          />
          <span className="text-white/40 text-xs">→</span>
          <input
            data-testid="export-to-date"
            type="date"
            value={toDate}
            onChange={(e)=>setToDate(e.target.value)}
            className="bg-slate-800 border border-white/10 rounded px-2 py-1 text-xs text-white"
          />
          <Button data-testid="export-reload-btn" size="sm" variant="outline" className="border-white/15 text-white h-8 text-xs" onClick={load}>Reload</Button>
          <Button data-testid="export-copy-btn" size="sm" variant="outline" className="border-white/15 text-white h-8 text-xs" onClick={copySummary}>
            <Copy className="w-3 h-3 mr-1" /> Copy summary
          </Button>
          <Button data-testid="export-print-btn" size="sm" className="bg-violet-600 hover:bg-violet-500 h-8 text-xs" onClick={() => window.print()}>
            <Printer className="w-3 h-3 mr-1" /> Print
          </Button>
        </div>
      </div>

      <div data-testid="doctor-export-page" className="max-w-4xl mx-auto px-8 py-8">
        {loading && (
          <div className="text-center text-slate-500 py-10">
            <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2" /> Loading…
          </div>
        )}
        {pageError && (
          <div className="rounded border border-amber-500 bg-amber-50 p-6 text-center">
            <AlertTriangle className="w-5 h-5 mx-auto text-amber-600 mb-2" />
            <div className="text-sm text-amber-900">{pageError}</div>
          </div>
        )}
        {!loading && !pageError && data && (
          <>
            <header className="mb-6 pb-4 border-b-2 border-slate-900">
              <div className="text-xs uppercase tracking-widest text-slate-500">ReGenflō NeuroFlow™ — PD Calibration</div>
              <h1 className="text-2xl font-semibold mt-1">Neurologist Report</h1>
              <div className="text-xs text-slate-600 mt-1">
                Subscriber: <span className="font-mono">{data.subscriber_id}</span>
                {data.profile?.diagnosis_label && <> · {data.profile.diagnosis_label}</>}
              </div>
              <div className="text-xs text-slate-600">
                Range: {data.from_date || '(all)'} → {data.to_date || '(all)'}
                · Generated: {new Date().toISOString().slice(0, 19).replace('T', ' ')}
              </div>
            </header>

            <Section title="Parkinson Profile">
              {data.profile ? (
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <D k="Diagnosis"          v={data.profile.diagnosis_label} />
                  <D k="Onset year"         v={data.profile.onset_year} />
                  <D k="Dominant side"      v={data.profile.dominant_side} />
                  <D k="Disease duration"   v={data.profile.disease_duration_years && `${data.profile.disease_duration_years} yrs`} />
                  <D k="Symptom dominance"  v={(data.profile.symptom_dominance || []).join(', ')} />
                  <D k="Family history"     v={data.profile.known_family_history} />
                  <D k="Constipation pre-motor" v={data.profile.constipation_before_motor_symptoms} />
                  <D k="Sleep baseline"     v={data.profile.sleep_quality_baseline} />
                  <D k="Morning stiffness"  v={data.profile.morning_stiffness_baseline} />
                  <D k="Baseline ON latency"  v={data.profile.baseline_on_latency_min && `${data.profile.baseline_on_latency_min} min`} />
                  <D k="Baseline ON duration" v={data.profile.baseline_on_duration_min && `${data.profile.baseline_on_duration_min} min`} />
                  <D k="Baseline dyskinesia"  v={data.profile.baseline_dyskinesia_score} />
                  <D k="OFF pattern"        v={data.profile.baseline_off_pattern} />
                  <D k="First OFF symptom"  v={data.profile.baseline_first_off_symptom} />
                </dl>
              ) : <Empty>No profile.</Empty>}
              {data.profile?.special_notes && (
                <div className="mt-3 text-sm">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Notes</div>
                  <p className="mt-1">{data.profile.special_notes}</p>
                </div>
              )}
            </Section>

            <Section title={`Medicine Anchors (${(data.medicine_anchors || []).length})`}>
              {data.medicine_anchors?.length ? (
                <Table head={['Time', 'Medicine', 'Type', 'Strength · Dose', 'Protected (b/a)', 'Flex (b/a)', 'Active']}>
                  {data.medicine_anchors.map(a => (
                    <tr key={a.id} className="border-b border-slate-200">
                      <Td>{a.planned_time}</Td>
                      <Td>{a.medicine_name}</Td>
                      <Td>{a.medicine_type}</Td>
                      <Td>{a.strength_label}{a.dose_label ? ` · ${a.dose_label}` : ''}</Td>
                      <Td>{a.protected_window_before_min}/{a.protected_window_after_min}m</Td>
                      <Td>{a.doctor_approved_flexibility_enabled ? `${a.flexibility_before_min}/${a.flexibility_after_min}m` : '—'}</Td>
                      <Td>{a.active ? 'yes' : 'no'}</Td>
                    </tr>
                  ))}
                </Table>
              ) : <Empty>No anchors.</Empty>}
            </Section>

            <Section title={`Daily snapshots (${(data.daily_snapshots || []).length})`}>
              {data.daily_snapshots?.length ? (
                <Table head={['Date', 'State', 'Gate', 'ON lat', 'OFF min', 'Delayed ON', 'No-ON', 'Dysk peak', 'Bowel']}>
                  {data.daily_snapshots.map(s => (
                    <tr key={s.id || s.date} className="border-b border-slate-200">
                      <Td>{s.date}</Td>
                      <Td>{s.current_state}</Td>
                      <Td>{s.gate_status}</Td>
                      <Td>{s.first_on_latency_min ?? '—'}</Td>
                      <Td>{s.total_off_time_min ?? '—'}</Td>
                      <Td>{s.delayed_on_count ?? 0}</Td>
                      <Td>{s.no_on_risk_count ?? 0}</Td>
                      <Td>{s.dyskinesia_peak_score ?? 0}</Td>
                      <Td>{s.bowel_status || '—'}</Td>
                    </tr>
                  ))}
                </Table>
              ) : <Empty>No snapshots in range.</Empty>}
            </Section>

            <Section title={`Events (${(data.events || []).length})`}>
              {data.events?.length ? (
                <Table head={['Time', 'Type', 'Severity', 'Meal/Stress', 'Note']}>
                  {data.events.map(e => (
                    <tr key={e.id} className="border-b border-slate-200">
                      <Td className="font-mono text-xs">{e.event_time}</Td>
                      <Td>{e.event_type}</Td>
                      <Td>{e.severity_score ?? '—'}</Td>
                      <Td>{e.meal_type || e.stress_type || '—'}</Td>
                      <Td>{e.note || '—'}</Td>
                    </tr>
                  ))}
                </Table>
              ) : <Empty>No events in range.</Empty>}
            </Section>

            <Section title={`Phase ladder (${(data.protocol_layers || []).length})`}>
              {data.protocol_layers?.length ? (
                <Table head={['#', 'Layer', 'Axis', 'Status', 'Start', 'Stable']}>
                  {data.protocol_layers.map(L => (
                    <tr key={L.id} className="border-b border-slate-200">
                      <Td>{L.layer_order}</Td>
                      <Td>{L.layer_name}</Td>
                      <Td>{L.axis}</Td>
                      <Td>{L.status}</Td>
                      <Td>{L.start_date || '—'}</Td>
                      <Td>{L.stable_date || '—'}</Td>
                    </tr>
                  ))}
                </Table>
              ) : <Empty>No layers.</Empty>}
            </Section>

            <Section title={`Safety alerts (${(data.safety_alerts || []).length})`}>
              {data.safety_alerts?.length ? (
                <Table head={['Created', 'Type', 'Severity', 'Status', 'Message']}>
                  {data.safety_alerts.map(a => (
                    <tr key={a.id} className="border-b border-slate-200">
                      <Td className="font-mono text-xs">{a.created_at}</Td>
                      <Td>{a.alert_type}</Td>
                      <Td>{a.severity}</Td>
                      <Td>{a.status}</Td>
                      <Td>{a.message}</Td>
                    </tr>
                  ))}
                </Table>
              ) : <Empty>No alerts in range.</Empty>}
            </Section>

            <footer className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500">
              {data.format_note}
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6 break-inside-avoid">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700 mb-2 pb-1 border-b border-slate-200">
        {title}
      </h2>
      {children}
    </section>
  );
}
function D({ k, v }) {
  return (
    <>
      <dt className="text-xs text-slate-500">{k}</dt>
      <dd className="text-sm">{v ?? '—'}</dd>
    </>
  );
}
function Table({ head, children }) {
  return (
    <table className="w-full text-sm">
      <thead className="border-b border-slate-300">
        <tr>{head.map((h) => <th key={h} className="text-left text-xs uppercase tracking-wider text-slate-500 py-1.5 pr-3">{h}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}
function Td({ children, className }) {
  return <td className={`py-1.5 pr-3 ${className || ''}`}>{children}</td>;
}
function Empty({ children }) {
  return <div className="text-sm text-slate-400 italic">{children}</div>;
}
